import React, { useMemo, useState } from 'react';
import {
  buildClinicalReportPayload,
  downloadClinicalReportPdf,
} from '../../utils/clinicalReportPdf';

export default function HairRoutineSection({
  hairResults,
  userData,
  aqiData,
  routineData,
  sessionId,
  isLoading = false,
}) {
  const [pdfBusy, setPdfBusy] = useState(false);

  const needsAttention = useMemo(() => {
    if (hairResults?.results?.all_detections || hairResults?.detections) {
      const dets = hairResults?.results?.all_detections || hairResults?.detections || [];

      const grouped = {};
      dets.forEach((d) => {
        const label = d.label;
        let baseSeverity = 30;

        const l = label.toLowerCase();
        if (l.includes('stage 4') || l.includes('spot baldness')) baseSeverity = 85;
        else if (l.includes('stage 3')) baseSeverity = 75;
        else if (l.includes('stage 2')) baseSeverity = 55;
        else if (l.includes('stage 1')) baseSeverity = 35;
        else if (l.includes('dandruff') || l.includes('rash')) baseSeverity = 50;
        else if (l.includes('pimple') || l.includes('bugs')) baseSeverity = 65;
        else if (l.includes('frizz') || l.includes('rough')) baseSeverity = 40;
        else if (l.includes('greasy') || l.includes('oil')) baseSeverity = 30;

        if (!grouped[label]) {
          grouped[label] = {
            label,
            baseSeverity,
            instances: 1,
            maxConf: d.confidence,
            color: hairResults.results?.color_map?.[label] || '#db2777',
          };
        } else {
          grouped[label].instances += 1;
          grouped[label].maxConf = Math.max(grouped[label].maxConf, d.confidence);
        }
      });

      return Object.values(grouped)
        .filter((d) => d.label !== 'Normal Scalp')
        .map((d) => {
          const persistenceBoost = Math.min(20, (d.instances - 1) * 5);
          const confFactor = (d.maxConf - 50) / 10;
          const finalValue = Math.min(100, d.baseSeverity + persistenceBoost + confFactor);

          return {
            label: d.label,
            value: finalValue,
            color: d.color,
            isPersistent: d.instances >= 2,
          };
        })
        .sort((a, b) => b.value - a.value)
        .slice(0, 3);
    }
    return [
      { label: 'Density', value: 75, color: '#ef4444', isPersistent: true },
      { label: 'Hairline', value: 60, color: '#f59e0b', isPersistent: true },
    ];
  }, [hairResults]);

  const aiSummary = useMemo(() => {
    const dets = hairResults?.results?.all_detections || hairResults?.detections || [];
    const topConcern = dets
      .filter((d) => d.label !== 'Normal Scalp')
      .sort((a, b) => b.confidence - a.confidence)[0]?.label;

    if (!topConcern) {
      return 'Primary Goal: Maintain healthy scalp balance and follicle strength.';
    }

    const goalMap = {
      'Spot Baldness': 'Restore localized density through micro-circulation and follicle stimulation.',
      Dandruff: 'Manage microbial imbalance and soothe the scalp barrier to restore pH stability.',
      'Thinning Stage 1': 'Strengthen existing hair shafts through protein-rich nourishment and scalp massage.',
      'Thinning Stage 2': 'Strengthen existing hair shafts through protein-rich nourishment and scalp massage.',
      'Thinning Stage 3': 'Strengthen existing hair shafts through protein-rich nourishment and scalp massage.',
      'Thinning Stage 4': 'Strengthen existing hair shafts through protein-rich nourishment and scalp massage.',
      'Scalp Rash': 'Reduce inflammation using calming botanicals and pH-balanced cleansing.',
      'Greasy Hair': 'Regulate sebum production to maintain a clean and balanced scalp environment.',
      'Rough/Straw Hair': 'Infuse deep moisture to repair cuticles and restore natural hair elasticity.',
    };

    const focus = goalMap[topConcern] || 'Restore natural scalp balance and follicle health.';
    return `Primary Goal: ${focus}`;
  }, [hairResults]);

  const hasHighSeverity = useMemo(
    () => needsAttention.some((item) => item.value >= 85 && item.isPersistent),
    [needsAttention]
  );

  const env = aqiData || { temp: 34.3, aqi: 'Moderate', uv: 'Extreme', location: 'Sholinganallur, Tamil Nadu' };

  if (isLoading) {
    return (
      <section id="routine" className="hair-report-routine">
        <div className="routine-section-skeleton">Loading personalized routine...</div>
      </section>
    );
  }

  return (
    <section id="routine" className="hair-report-routine">
      <div className="hair-routine-page parity-ui">
      <main className="routine-body-layout">
        <div className="top-meta-cards top-meta-cards--env-only">
          <div className="env-meta-card">
            <h4 className="meta-card-label">ENVIRONMENT</h4>
            <div className="env-metrics-flex">
              <div className="env-metric-item">
                <span className="m-icon">🌡️</span>
                <span className="m-val">{env.temp}°C</span>
              </div>
              <div className="env-metric-item">
                <span className="m-label">AQI:</span>
                <span className="m-val stat-moderate">{env.aqi}</span>
              </div>
              <div className="env-metric-item">
                <span className="m-label">UV Index:</span>
                <span className="m-val stat-extreme">{env.uv}</span>
              </div>
            </div>
            <div className="location-footer">
              <span className="loc-icon">📍</span> {env.location}
            </div>
          </div>
        </div>

        <section className="dashboard-section severity-dashboard">
          <h3 className="dashboard-section-title">Critical Attention Areas</h3>
          <p className="severity-explanation">Synthesis of all 4 image viewpoints identifying conditions requiring immediate intervention.</p>

          {hasHighSeverity && (
            <div className="medical-urgency-alert-premium">
              <div className="alert-pill-clinical">PERSISTENT CLINICAL MARKERS</div>
              <div className="alert-content-clinical">
                <div className="alert-icon-box">👨‍⚕️</div>
                <div className="alert-text-box">
                  <h4>Clinical Consultation Recommended</h4>
                  <p>
                    AI Synthesis has identified <strong>{needsAttention.filter((i) => i.value >= 85).map((i) => i.label).join(', ')}</strong> as a persistent clinical marker across multiple image viewpoints.
                    Because these findings are consistent and high-impact, we recommend a professional evaluation by a trichologist to rule out follicle-deep issues.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="severity-circles-container">
            {needsAttention.map((item, idx) => (
              <div key={idx} className="severity-circle-wrapper">
                <div className="circle-svg-box">
                  <svg viewBox="0 0 36 36" className="circular-progress-svg">
                    <path
                      className="circle-track"
                      fill="none"
                      stroke="#f1f5f9"
                      strokeWidth="3"
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path
                      className="circle-fill-path"
                      fill="none"
                      stroke={item.color}
                      strokeWidth="3.2"
                      strokeDasharray={`${item.value}, 100`}
                      strokeLinecap="round"
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <text x="18" y="20.35" className="circle-value-text severity-text-color">{item.value > 70 ? 'High' : item.value > 40 ? 'Med' : 'Low'}</text>
                  </svg>
                </div>
                <span className="circle-label-text">{item.label}</span>
              </div>
            ))}
          </div>
        </section>

        <div className="routine-main-grid">
          <section className="routine-card routine-insight-card">
            <div className="card-header-with-icon">
              <span className="header-icon">🎯</span>
              <h3 className="card-heading">Clinical Focus</h3>
            </div>
            <p className="insight-text">{aiSummary}</p>
          </section>

          <section className="routine-card timeline-card full-width-timeline">
            <div className="card-header-with-icon">
              <span className="header-icon">⏰</span>
              <h3 className="card-heading">Daily Schedule</h3>
            </div>
            <div className="timeline-flex">
              <div className="timeline-period">
                <div className="period-header morning">☀️ Morning</div>
                <ul className="timeline-list">
                  {routineData?.morning_routine?.map((item, i) => <li key={i}>{item}</li>) || (
                    <>
                      <li>Cleanse scalp with pH-balanced shampoo</li>
                      <li>Apply lightweight protective spray</li>
                      <li>Detangle using wide-tooth comb</li>
                    </>
                  )}
                </ul>
              </div>
              <div className="timeline-period">
                <div className="period-header evening">🌙 Evening</div>
                <ul className="timeline-list">
                  {routineData?.evening_routine?.map((item, i) => <li key={i}>{item}</li>) || (
                    <>
                      <li>Massage scalp with revitalizing oil</li>
                      <li>Apply leave-in hydration cream</li>
                      <li>Use silk wrap for friction reduction</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </section>
        </div>

        <section className="lifestyle-wellness-section">
          <h3 className="dashboard-section-title">Lifestyle & Nutrition Support</h3>
          <div className="wellness-grid-three">
            <div className="wellness-card">
              <div className="w-icon">🥗</div>
              <h4>Dietary Focus</h4>
              <p>Increase intake of Biotin-rich foods (eggs, nuts) and Omega-3 fatty acids to support follicle membrane integrity.</p>
            </div>
            <div className="wellness-card">
              <div className="w-icon">💧</div>
              <h4>Hydration Goal</h4>
              <p>Target 2.5L daily. Dehydration directly impacts scalp elasticity and can lead to brittle hair shafts.</p>
            </div>
            <div className="wellness-card">
              <div className="w-icon">🧘</div>
              <h4>Stress Management</h4>
              <p>Elevated cortisol levels can disrupt the hair growth cycle. We recommend 10 mins of mindful breathing daily.</p>
            </div>
          </div>
        </section>

        <div className="results-cta-container multi-action-footer">
          <button
            type="button"
            className="premium-action-btn-wide accent-teal"
            disabled={pdfBusy}
            onClick={async () => {
              try {
                setPdfBusy(true);
                const payload = buildClinicalReportPayload({
                  hairResults,
                  aqiData,
                  routineData,
                  sessionId,
                });
                await downloadClinicalReportPdf(payload);
              } catch (err) {
                console.error(err);
                alert(err?.message || 'Could not download PDF report');
              } finally {
                setPdfBusy(false);
              }
            }}
          >
            {pdfBusy ? 'GENERATING PDF…' : 'DOWNLOAD PDF REPORT'}
          </button>
        </div>
      </main>
      </div>
    </section>
  );
}
